import React, { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { authApi } from '@api/auth'
import { ResetPasswordData } from '@types'
import { apiErrorMessage } from '@api/index'

const ResetPassword: React.FC = () => {
  const navigate = useNavigate()
  // The emailed link is /auth/reset-password/{uid}/{token}/ - both path
  // segments, kept separate (not joined into one string) since the token
  // itself can contain a hyphen.
  const { uid = '', token = '' } = useParams<{ uid: string; token: string }>()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { control, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordData>({
    defaultValues: {
      uid,
      token,
      new_password: '',
      new_password_confirm: '',
    },
  })

  const newPassword = watch('new_password')

  const onSubmit = async (data: ResetPasswordData) => {
    setIsLoading(true)
    setError('')

    try {
      await authApi.resetPassword(data)
      navigate('/auth/sign-in', {
        state: { message: 'Password reset successfully. Please sign in with your new password.' }
      })
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to reset password. The link may have expired.'))
    } finally {
      setIsLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  if (!uid || !token) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          Invalid reset link. Please request a new password reset.
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Link to="/auth/forgot-password">
            <Button variant="contained">
              Request New Reset
            </Button>
          </Link>
        </Box>
      </Box>
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography
        variant="h4"
        component="h1"
        align="center"
        gutterBottom
        sx={{ fontWeight: 'bold', color: 'primary.main' }}
      >
        Reset Password
      </Typography>

      <Typography
        variant="body1"
        align="center"
        color="textSecondary"
        sx={{ mb: 3 }}
      >
        Enter your new password below
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Controller
        name="new_password"
        control={control}
        rules={{
          required: 'Password is required',
          minLength: {
            value: 8,
            message: 'Password must be at least 8 characters',
          },
          pattern: {
            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
            message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
          },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            error={!!errors.new_password}
            helperText={errors.new_password?.message}
            margin="normal"
            autoComplete="new-password"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Controller
        name="new_password_confirm"
        control={control}
        rules={{
          required: 'Please confirm your password',
          validate: (value) => value === newPassword || 'Passwords do not match',
        }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            error={!!errors.new_password_confirm}
            helperText={errors.new_password_confirm?.message}
            margin="normal"
            autoComplete="new-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleConfirmPasswordVisibility}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        sx={{ mt: 3, mb: 2, py: 1.5 }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Resetting Password...
          </>
        ) : (
          'Reset Password'
        )}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link
          to="/auth/sign-in"
          style={{
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Typography variant="body2" color="primary">
            Back to Sign In
          </Typography>
        </Link>
      </Box>
    </Box>
  )
}

export default ResetPassword
